/** The Genetics section on an animal's Records tab: registration, mini pedigree, EPDs, DNA, makeup and stock. */
import { useState } from "react";
import { Link } from "react-router";
import type { AnimalDetail } from "../../../contract";
import { openRecord } from "../../../web/forms/EventForm";
import { formatDate } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { useApi } from "../../../web/lib/query";
import { useCan } from "../../../web/lib/session";
import { Button, Card, Chip, ErrorNote, Icon, Loading, Segmented } from "../../../web/ui/kit";
import { CONDITION_RESULTS, PARENT_OUTCOMES } from "../contract/events";
import { associationKey, associationShort } from "../shared/epd";
import { DNA_STATUS_LABELS, JOB_STAGE_LABELS } from "../shared/labels";
import { percent } from "../shared/pedigree";
import type { AnimalGeneticsDTO, GeneticsState } from "../shared/types";
import { PedigreeView } from "./PedigreeTree";
import { CompositionBar, EpdGrid, RunLabel, useTraits } from "./ui";

export function showGenetics(a: AnimalDetail): boolean {
  return !!a.facets.genetics || a.status === "reference" || a.identifiers.some((i) => i.scheme === "registration" && !i.valid_to);
}

function RegistrationLine({ g, dto }: { g: GeneticsState | null; dto: AnimalGeneticsDTO }) {
  const current = dto.identifiers.filter((i) => !i.valid_to);
  const pending = g?.registrations?.filter((r) => r.status !== "registered") ?? [];
  if (!current.length && !pending.length) return <p className="muted">{t("Not registered")}</p>;
  return (
    <div className="stack" style={{ gap: 6 }}>
      {current.map((i) => <div key={i.id} className="genetics-row-head"><Icon name="award" size={16} /><strong className="mono">{i.value}</strong>{i.issuer && <span className="muted">{associationShort(i.issuer)}</span>}</div>)}
      {pending.filter((r) => !current.some((c) => c.issuer && associationKey(c.issuer) === associationKey(r.association))).map((r) => (
        <div key={r.association} className="genetics-row-head">
          <Chip tone={r.status === "rejected" || r.status === "pending" ? "warn" : "genetics"} icon="award">
            {r.status === "submitted" ? t("Sent to {{association}}", { association: associationShort(r.association) })
              : r.status === "rejected" ? t("Rejected by {{association}}", { association: associationShort(r.association) })
              : r.status === "pending" ? t("On hold at {{association}}", { association: associationShort(r.association) })
              : t("Recorded with {{association}}", { association: associationShort(r.association) })}
          </Chip>
          {r.reason && <span className="muted">{r.reason}</span>}
        </div>
      ))}
    </div>
  );
}

function DnaBlock({ g }: { g: GeneticsState }) {
  const d = g.dna;
  const r = g.dna_result;
  if (!d) return null;
  const flagged = (r?.conditions ?? []).filter((c) => c.result !== "free");
  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className="genetics-row-head">
        <Chip tone={d.status === "failed" ? "warn" : "genetics"} icon="dna">{t(DNA_STATUS_LABELS[d.status])}</Chip>
        <span className="muted">{[d.lab, d.on ? formatDate(d.on) : null, d.barcode ? t("barcode {{barcode}}", { barcode: d.barcode }) : null].filter(Boolean).join(" · ")}</span>
      </div>
      {r && r.sample_ok && (
        <ul className="genetics-row-meta" style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {r.sire && <li>{t("Sire check")}: <strong>{t(PARENT_OUTCOMES[r.sire])}</strong></li>}
          {r.dam && <li>{t("Dam check")}: <strong>{t(PARENT_OUTCOMES[r.dam])}</strong></li>}
          {r.call_rate !== undefined && <li>{t("Call rate")}: {r.call_rate}%</li>}
          {r.genomic && <li>{t("Genomic EPDs released")}</li>}
          {r.conditions.length > 0 && !flagged.length && <li>{t("Conditions tested free")}: {r.conditions.map((c) => c.condition).join(", ")}</li>}
          {flagged.map((c) => <li key={c.condition}><strong>{c.condition}</strong>: {t(CONDITION_RESULTS[c.result])}</li>)}
        </ul>
      )}
    </div>
  );
}

export function GeneticsSection({ animal }: { animal: AnimalDetail }) {
  useLanguage();
  const can = useCan();
  const q = useApi<AnimalGeneticsDTO>(`/m/genetics/animals/${animal.id}`);
  const traits = useTraits();
  const [run, setRun] = useState<string | null>(null);
  const [history, setHistory] = useState(false);
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  const dto = q.data;
  const g = dto.facet;
  const latest = g?.evaluations ?? [];
  const shown = latest.find((s) => `${s.association}|${s.kind}` === run) ?? latest[0];
  const older = dto.evaluations.filter((s) => shown && s.event_id !== shown.event_id && associationKey(s.association) === associationKey(shown.association));
  const record = (type: string, data?: Record<string, unknown>) => openRecord({ initial: { type, subjects: [{ kind: "animal", id: animal.id, role: "animal", label: animal.label }], data }, onDone: () => void q.refetch() });
  return (
    <div className="stack">
      <Card>
        <div className="stack">
          <RegistrationLine g={g} dto={dto} />
          <div className="genetics-row-meta">
            <span>{plural(dto.progeny.total, "{{count}} calf on record", "{{count}} calves on record")}</span>
            {dto.progeny.registered > 0 && <span>{plural(dto.progeny.registered, "{{count}} registered", "{{count}} registered")}</span>}
            {dto.inbreeding.f > 0 && <span>{t("Inbreeding {{value}}", { value: percent(dto.inbreeding.f) })}</span>}
            {g?.cg && Object.entries(g.cg).map(([stage, v]) => v && <span key={stage}>{t("{{stage}} group {{code}}", { stage: t(stage === "birth" ? "Birth" : stage === "weaning" ? "Weaning" : "Yearling"), code: v.code })}</span>)}
          </div>
        </div>
      </Card>

      <div className="stack" style={{ gap: 8 }}>
        <div className="section-head"><h3>{t("Pedigree")}</h3><Link to={`/work/genetics/pedigree/${animal.id}`} className="link">{t("Full pedigree")}<Icon name="chevron" size={16} /></Link></div>
        <PedigreeView animalId={animal.id} defaultDepth={2} showRoot={false} pickDepth={false} />
        {can("record") && <div className="row-actions"><Button size="sm" variant="ghost" icon="edit" onClick={() => record("genetics.pedigree_correction", { role: "sire" })}>{t("Correct pedigree")}</Button></div>}
      </div>

      {shown && (
        <div className="stack" style={{ gap: 8 }}>
          <div className="section-head">
            <h3>{t("EPDs")}</h3>
            {can("record") && <Button size="sm" variant="ghost" icon="plus" onClick={() => record("genetics.evaluation", { association: shown.association })}>{t("Record EPDs")}</Button>}
          </div>
          <Card>
            <div className="stack">
              {latest.length > 1 && <Segmented label={t("Association")} value={`${shown.association}|${shown.kind}`} onChange={setRun} options={latest.map((s) => ({ value: `${s.association}|${s.kind}`, label: associationShort(s.association) }))} />}
              <RunLabel s={shown} />
              <EpdGrid snapshot={shown} traits={traits.data?.traits} />
              {older.length > 0 && (
                <div>
                  <button type="button" className="link" onClick={() => setHistory(!history)}>{history ? t("Hide earlier runs") : plural(older.length, "Show {{count}} earlier run", "Show {{count}} earlier runs")}</button>
                  {history && older.map((s) => <div key={s.event_id} className="stack" style={{ marginTop: 12 }}><RunLabel s={s} /><EpdGrid snapshot={s} traits={traits.data?.traits} /></div>)}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {g?.dna && (
        <div className="stack" style={{ gap: 8 }}>
          <div className="section-head">
            <h3>{t("DNA")}</h3>
            {can("record") && (g.dna.status === "failed" || g.dna.status === "resulted") && <Button size="sm" variant="ghost" icon="dna" onClick={() => record("genetics.dna_sample", { sample_type: g.dna?.sample_type ?? "hair" })}>{t("DNA sample")}</Button>}
          </div>
          <Card><DnaBlock g={g} /></Card>
        </div>
      )}

      {g?.composition && (
        <div className="stack" style={{ gap: 8 }}>
          <div className="section-head">
            <h3>{t("Breed makeup")}</h3>
            {can("record") && <Button size="sm" variant="ghost" icon="edit" onClick={() => record("genetics.breed_composition", { components: g.composition!.components, basis: g.composition!.basis })}>{t("Update")}</Button>}
          </div>
          <Card><CompositionBar components={g.composition.components} unknown={g.composition.unknown} /></Card>
        </div>
      )}

      {can("record") && (!shown || !g?.dna || !g?.composition) && (
        <div className="stack" style={{ gap: 6 }}>
          <span className="eyebrow">{t("Add")}</span>
          <div className="row-actions">
            {!shown && <Button size="sm" icon="report" onClick={() => record("genetics.evaluation")}>{t("EPDs")}</Button>}
            {!g?.dna && <Button size="sm" icon="dna" onClick={() => record("genetics.dna_sample", { sample_type: "hair" })}>{t("DNA sample")}</Button>}
            {!g?.composition && <Button size="sm" icon="layers" onClick={() => record("genetics.breed_composition")}>{t("Breed makeup")}</Button>}
          </div>
          {!g?.composition && animal.breed && <p className="muted">{t("Recorded breed: {{breed}}. No percentages yet.", { breed: animal.breed })}</p>}
        </div>
      )}

      {(dto.semen || dto.embryos || g?.donor || g?.collections) && (
        <Card to="/ranch/genetic-inventory">
          <div className="genetics-row-head">
            <Icon name="tank" size={18} />
            <span className="genetics-row-title">{t("Semen and embryos")}</span>
            <Icon name="chevron" size={18} />
          </div>
          <div className="genetics-row-meta">
            {dto.semen && <span>{plural(dto.semen.on_hand, "{{count}} straw on hand", "{{count}} straws on hand")}{dto.semen.reserved ? ` · ${plural(dto.semen.reserved, "{{count}} held for plans", "{{count}} held for plans")}` : ""}</span>}
            {dto.embryos && <span>{plural(dto.embryos.on_hand, "{{count}} embryo in the tank", "{{count}} embryos in the tank")}</span>}
            {g?.donor && <span>{plural(g.donor.flushes, "{{count}} flush", "{{count}} flushes")}</span>}
            {g?.collections && <span>{plural(g.collections.count, "{{count}} collection", "{{count}} collections")}</span>}
          </div>
        </Card>
      )}

      {dto.batches.length > 0 && (
        <div className="stack" style={{ gap: 8 }}>
          <h3>{t("In genetics work")}</h3>
          <ul className="list">
            {dto.batches.map((b) => <li key={b.id} className="row"><Link to={`/work/jobs/${b.id}`} className="row-inner"><span className="row-main"><span className="row-title">{b.title}</span><span className="row-meta">{b.stage ? t(JOB_STAGE_LABELS[b.stage] ?? b.stage) : ""}</span></span><Icon name="chevron" size={18} /></Link></li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
